import type { FC, PropsWithChildren } from "react";
import { useMemo } from "react";

import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { getLeadDomainFromRow } from "app/pages/sales-intelligence/sub-modules/company-search/helpers/rows";
import SignalsTechnologiesTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/tooltips/SignalsTechnologiesTooltip/SignalsTechnologiesTooltip";
import type {
    COLUMN_TECHNOLOGIES_ADDED_NAME,
    COLUMN_TECHNOLOGIES_REMOVED_NAME,
} from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL } from "app/pages/sales-intelligence/sub-modules/signals/constants";
import type TechnologySignalRowDto from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalRowDto";
import type TechnologySignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalSubType";
import createSignalsTooltipViewModelFromArray from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/common/factory";
import type SignalsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/SignalsTooltipViewModel";
import createTooltipColumnsResolver from "app/pages/sales-intelligence/sub-modules/signals/services/table-columns/technology/tooltipColumnsFactory";
import { compareItemsByDateAsc, compareItemsByDateDesc } from "app/pages/sales-intelligence/utils/sort";

interface ISignalsTechnologyTooltipContainerProps {
    field:
        | typeof COLUMN_TECHNOLOGIES_ADDED_NAME
        | typeof COLUMN_TECHNOLOGIES_REMOVED_NAME
        | typeof COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL;
    value: readonly TechnologySignalRowDto[];
    onViewAll: () => void;
    row: any;
    signalSubType: TechnologySignalSubType;
}

export const SignalsTechnologyTooltipContainer: FC<PropsWithChildren<ISignalsTechnologyTooltipContainerProps>> = ({
    children,
    row,
    value,
    onViewAll,
    field,
    signalSubType,
}) => {
    const leadDomain = getLeadDomainFromRow(row);

    const sortedTechnologies = useMemo<TechnologySignalRowDto[]>(() => {
        if (!value) {
            return [];
        }

        const sortingFunc =
            field === COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL ? compareItemsByDateAsc : compareItemsByDateDesc;

        return value.slice().sort((a, b) => sortingFunc(a.date, b.date));
    }, [value]);

    if (sortedTechnologies.length === 0) {
        return <>{children}</>;
    }

    const redirectLink = siNavigator.getWebsiteAnalysisTechnologiesLink(leadDomain, "All");

    const createViewModel = (itemsLimit: number): SignalsTooltipViewModel => {
        const tableColumnsResolver = createTooltipColumnsResolver({
            parentColumnName: field,
        });

        return createSignalsTooltipViewModelFromArray<TechnologySignalRowDto>({
            tableColumnsResolver,
            values: sortedTechnologies,
            itemsLimit,
        });
    };

    return (
        <SignalsTechnologiesTooltip
            signalSubType={signalSubType}
            redirectLink={redirectLink}
            createViewModel={createViewModel}
            onViewAll={onViewAll}
        >
            <>{children}</>
        </SignalsTechnologiesTooltip>
    );
};
